/**
 * Deploy-phase selection visuals: the ring around a selectable planet and the
 * landing-zone marker. Pure Three.js construction; the deploy controller
 * decides when they appear.
 */

import * as THREE from 'three';
import type { PlanetState, Vec3 } from '../types';
import { getSurfaceRadius } from '../core/surface';
import { disposeObjectTree } from './meshBake';

/** Translucent billboard ring just outside the planet's radius. */
export function createDeployHighlight(scene: THREE.Scene, planet: PlanetState, cameraPos: THREE.Vector3): THREE.Mesh {
  const geo = new THREE.RingGeometry(planet.radius * 1.1, planet.radius * 1.2, 32);
  const mtl = new THREE.MeshBasicMaterial({ color: '#66aa88', transparent: true, opacity: 0.5, side: THREE.DoubleSide });
  const ring = new THREE.Mesh(geo, mtl);
  ring.position.set(planet.position.x, planet.position.y, planet.position.z);
  ring.lookAt(cameraPos);
  scene.add(ring);
  return ring;
}

/** Landing-zone marker: a team-colored surface ring, a white dot and a beacon line along the surface normal. */
export function createDeploySpawnMarker(scene: THREE.Scene, planet: PlanetState, surfaceDir: Vec3, teamColor: string): THREE.Group {
  const marker = new THREE.Group();

  const ring = new THREE.Mesh(
    new THREE.RingGeometry(planet.radius * 0.06, planet.radius * 0.08, 16),
    new THREE.MeshBasicMaterial({ color: teamColor, transparent: true, opacity: 0.8, side: THREE.DoubleSide }),
  );
  marker.add(ring);

  const dot = new THREE.Mesh(
    new THREE.CircleGeometry(planet.radius * 0.02, 8),
    new THREE.MeshBasicMaterial({ color: '#ffffff', transparent: true, opacity: 0.9, side: THREE.DoubleSide }),
  );
  marker.add(dot);

  const beacon = new THREE.Mesh(
    new THREE.CylinderGeometry(0.02, 0.02, planet.radius * 0.5, 4),
    new THREE.MeshBasicMaterial({ color: teamColor, transparent: true, opacity: 0.5 }),
  );
  beacon.position.y = planet.radius * 0.25;
  marker.add(beacon);

  // Sit on the actual terrain so ridged planets don't swallow the marker
  const sr = getSurfaceRadius(planet, surfaceDir) + 0.1;
  marker.position.set(
    planet.position.x + surfaceDir.x * sr,
    planet.position.y + surfaceDir.y * sr,
    planet.position.z + surfaceDir.z * sr,
  );
  marker.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), new THREE.Vector3(surfaceDir.x, surfaceDir.y, surfaceDir.z));

  scene.add(marker);
  return marker;
}

/** Remove a deploy visual from the scene and free its GPU resources. */
export function removeDeployVisual(scene: THREE.Scene, obj: THREE.Object3D): void {
  disposeObjectTree(obj);
  scene.remove(obj);
}
